/**
 * AppLayout.tsx — Authenticated SaaS app zone shell
 *
 * Purpose:    Sidebar nav (desktop) / top bar (mobile) for the ɳSentry SaaS
 *             app zone (Monitors, Incidents, Status Pages, Overview, API Keys,
 *             Billing) plus signed-in user email + sign-out.
 * Inputs:     child routes via <Outlet />; useAuth() for user + signOut
 * Outputs:    Chrome + routed page content
 * Constraints: No SSR; client-side SPA only. Dark-first per app convention.
 * SOT:        nsentry SaaS app zone
 */
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'

const NAV_ITEMS = [
  { to: '/app/overview', label: 'Overview' },
  { to: '/app/monitors', label: 'Monitors' },
  { to: '/app/incidents', label: 'Incidents' },
  { to: '/app/errors', label: 'Errors' },
  { to: '/app/rum', label: 'RUM' },
  { to: '/app/cron', label: 'Cron' },
  { to: '/app/status-pages', label: 'Status Pages' },
  { to: '/app/api-keys', label: 'API Keys' },
  { to: '/app/team', label: 'Team' },
  { to: '/app/billing', label: 'Billing' },
  { to: '/app/settings', label: 'Settings' },
] as const

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-sky-900/50 text-sky-700 dark:text-sky-300' : 'text-brand-muted hover:bg-brand-surface-2 hover:text-white',
  ].join(' ')
}

export function AppLayout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    navigate('/app/login')
  }

  return (
    <div className="flex min-h-screen flex-col bg-brand-bg lg:flex-row">
      {/* Mobile top bar */}
      <header className="flex items-center justify-between border-b border-brand-subtle bg-brand-surface px-4 py-3 lg:hidden">
        <span className="text-sm font-semibold text-sky-700 dark:text-sky-400">ɳSentry</span>
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500"
        >
          Sign out
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden w-56 flex-shrink-0 border-r border-brand-subtle bg-brand-surface lg:flex lg:flex-col">
        <div className="flex h-14 items-center border-b border-brand-subtle px-4">
          <span className="text-sm font-semibold text-sky-700 dark:text-sky-400">ɳSentry</span>
        </div>
        <nav className="flex-1 space-y-0.5 px-2 py-4" aria-label="App navigation">
          {NAV_ITEMS.map(({ to, label }) => (
            <NavLink key={to} to={to} className={navLinkClass}>
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-2 border-t border-brand-subtle px-4 py-3">
          <p className="truncate text-xs text-brand-muted" title={user?.email}>
            {user?.email}
          </p>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full rounded-md border border-brand-card px-3 py-1.5 text-xs font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500"
          >
            Sign out
          </button>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-2 gap-y-0.5 pt-1 text-[11px] text-brand-muted">
            <NavLink to="/legal/terms" className="hover:text-brand-muted">Terms</NavLink>
            <span aria-hidden="true">·</span>
            <NavLink to="/legal/privacy" className="hover:text-brand-muted">Privacy</NavLink>
            <span aria-hidden="true">·</span>
            <NavLink to="/legal/gdpr" className="hover:text-brand-muted">GDPR</NavLink>
          </nav>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  )
}
